import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
// material
import {
  Box,
  Typography,
  Button,
  Badge,
  TextField,
  InputAdornment,
} from '@mui/material';
import PivotTableChartIcon from '@mui/icons-material/PivotTableChart';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
// Removed deprecated makeStyles import
import {
  DataGridPremium,
  Toolbar,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  QuickFilter,
  QuickFilterControl,
  QuickFilterClear,
  ExportCsv,
  useGridRootProps,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
  gridFilterActiveItemsSelector,
  PivotPanelTrigger,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid-premium';
// components
import Page from 'src/components/Page';
import LottieLoadingOverlay from 'src/components/LottieLoadingOverlay';
// hooks
import { useCheckAction } from 'src/utils/permissions';
import { MBreadcrumbs } from 'src/components/@material-extend';
import { useTranslation } from 'react-i18next';
import { getDataGridLocaleText } from 'src/locales/libraryLocales';
import useOpenRowDataGrid from 'src/hooks/useOpenRowDataGrid';
import usePersistentGridSettings from 'src/hooks/usePersistentGridSettings';
import useTableKey from './hooks/useTableKey';
import useGridFilterSources from './hooks/useGridFilterSources';
import useGridFilterDraftState from './hooks/useGridFilterDraftState';
import useInitialServerFilterSync from './hooks/useInitialServerFilterSync';
import useInitialServerSortSync from './hooks/useInitialServerSortSync';
import useGridSortState from './hooks/useGridSortState';
import useGridColumnState from './hooks/useGridColumnState';

// ----------------------------------------------------------------------

// Removed deprecated makeStyles - now using sx prop directly

// ----------------------------------------------------------------------

function CustomGridToolbarExport({ onClick: _, customExport, ...other }) {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();

  const handleClick = useCallback(
    (event) => {
      event.preventDefault();

      if (customExport) {
        customExport();
      }
    },
    [customExport]
  );

  const BaseButton = rootProps.slots?.baseButton || Button;
  return (
    <BaseButton
      color="primary"
      size="small"
      startIcon={<FileDownloadIcon />}
      {...other}
      onClick={handleClick}
      {...rootProps.slotProps?.baseButton}
    >
      {apiRef.current.getLocaleText('toolbarExport')}
    </BaseButton>
  );
}

const CustomToolbar = React.forwardRef(function ToolbarImpl(props, ref) {
  const {
    showExport,
    customExport,
    onResetView,
    showQuickFilter,
    quickFilterProps: toolbarQuickFilterProps,
    ...other
  } = props;
  const rootProps = useGridRootProps();
  const apiRef = useGridApiContext();
  const { t } = useTranslation('translation');

  const activeFilters = useGridSelector(apiRef, gridFilterActiveItemsSelector);
  const filterCount = activeFilters?.length ?? 0;

  const canExport = useCheckAction('export');

  if (
    rootProps.hideToolbar ||
    (rootProps.disableColumnFilter &&
      rootProps.disableColumnSelector &&
      rootProps.disableDensitySelector)
  ) {
    return null;
  }

  return (
    <Toolbar
      ref={ref}
      {...other}
      sx={{ gap: 1, p: 1, justifyContent: 'flex-start', flexWrap: 'wrap' }}
    >
      <ColumnsPanelTrigger
        render={<Button size="small" startIcon={<ViewColumnIcon />} />}
      >
        {t('table.columns', { defaultValue: 'Columns' })}
      </ColumnsPanelTrigger>
      {rootProps.disableColumnFilter ? null : (
        <FilterPanelTrigger
          render={
            <Button
              size="small"
              startIcon={
                <Badge badgeContent={filterCount} color="primary">
                  <FilterListIcon />
                </Badge>
              }
            />
          }
        >
          {t('table.filters', { defaultValue: 'Filters' })}
        </FilterPanelTrigger>
      )}
      <GridToolbarDensitySelector />

      {rootProps.pivoting ? (
        <PivotPanelTrigger
          render={<Button size="small" startIcon={<PivotTableChartIcon />} />}
        >
          {t('table.pivot', { defaultValue: 'Pivot' })}
        </PivotPanelTrigger>
      ) : null}

      {canExport && showExport ? (
        customExport ? (
          <CustomGridToolbarExport customExport={customExport} />
        ) : (
          <ExportCsv
            render={
              <Button size="small" startIcon={<FileDownloadIcon />}>
                {t('table.export', { defaultValue: 'Export' })}
              </Button>
            }
          />
        )
      ) : null}
      <Box flex="1" />

      {onResetView ? (
        <Button size="small" onClick={onResetView}>
          {t('table.reset-view')}
        </Button>
      ) : null}

      {showQuickFilter ? (
        <QuickFilter {...toolbarQuickFilterProps}>
          <QuickFilterControl
            render={(props, ref) => (
              <TextField
                {...props}
                inputRef={ref}
                variant="standard"
                size="small"
                placeholder={t('table.search', { defaultValue: 'Search…' })}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  width: { xs: 150, sm: 200 },
                  '& .MuiInput-underline:before': {
                    borderBottomColor: 'divider',
                  },
                }}
              />
            )}
          />
          <QuickFilterClear
            render={(props, ref) => (
              <Button
                {...props}
                ref={ref}
                size="small"
                sx={{ minWidth: 'auto', p: 0.5 }}
              >
                <ClearIcon fontSize="small" />
              </Button>
            )}
          />
        </QuickFilter>
      ) : null}
    </Toolbar>
  );
});

const TableGrid = ({
  rows,
  columns,
  isLoading,
  serverSideFiltering,
  setFilterModel,
  serverSideSorting,
  setSortModel,
  other,
  showExport,
  height,
  gridMinHeight = '400px',
  slots: directSlots,
  pivoting,
}) => {
  const { i18n } = useTranslation();
  const currentLocale = i18n.language;
  const location = useLocation();

  const {
    tableKey: providedTableKey,
    columnVisibilityModel: providedColumnVisibilityModel,
    filterModel: parentFilterModel,
    sortModel: providedSortModel,
    initialState: providedInitialState,
    disableColumnFilter,
    ...tableProps
  } = other ?? {};

  const { tableKey } = useTableKey({
    providedTableKey,
    pathname: location?.pathname,
  });

  const defaultVisibility = useMemo(
    () =>
      columns.reduce(
        (a, c) => ({
          ...a,
          [c.field]: !c.hide,
        }),
        {}
      ),
    [columns]
  );

  const defaultColumnWidths = useMemo(
    () =>
      columns.reduce((acc, col) => {
        if (col.width != null) {
          acc[col.field] = col.width;
        }
        return acc;
      }, {}),
    [columns]
  );

  const defaultConfig = useMemo(
    () => ({
      columnVisibilityModel: providedColumnVisibilityModel
        ? { ...defaultVisibility, ...providedColumnVisibilityModel }
        : defaultVisibility,
      filterModel: parentFilterModel,
      sortModel: providedSortModel ?? providedInitialState?.sorting?.sortModel,
      columnOrder: columns.map((c) => c.field),
      columnWidths: defaultColumnWidths,
    }),
    [
      columns,
      defaultVisibility,
      defaultColumnWidths,
      providedColumnVisibilityModel,
      parentFilterModel,
      providedSortModel,
      providedInitialState,
    ]
  );

  const {
    settings,
    loading: settingsLoading,
    updateSettings,
  } = usePersistentGridSettings({
    tableKey,
    defaultConfig,
  });

  const { effectiveFilterModel } = useGridFilterSources({
    disableColumnFilter,
    parentFilterModel,
    persistedFilterModel: settings.filterModel,
    validColumnFields: new Set(columns.map((col) => col.field)),
  });

  const { gridFilterModel, handleFilterModelChange } = useGridFilterDraftState({
    initialFilterModel: effectiveFilterModel,
    serverSideFiltering,
    setFilterModel,
    updateSettings,
  });

  useInitialServerFilterSync({
    effectiveFilterModel,
    parentFilterModel,
    serverSideFiltering,
    setFilterModel,
    settingsLoading,
    tableKey,
  });

  const {
    pageSizeOptions: providedPageSizeOptions,
    paginationModel: providedPaginationModel,
    onPaginationModelChange: providedOnPaginationModelChange,
    apiRef: providedApiRef,
    disableVirtualization: providedDisableVirtualization,
    onScrollPositionChange: providedOnScrollPositionChange,
    disableColumnVirtualization: providedDisableColumnVirtualization,
    slots: providedSlots,
    components: providedComponents,
    slotProps: providedSlotProps,
    componentsProps: providedComponentsProps,
    paginationMode,
    rowCount,
    onStateChange: providedOnStateChange,
    showQuickFilter: showQuickFilterProp,
    quickFilterProps: quickFilterPropsProp,
    sx: providedSx,
    ...restOther
  } = tableProps ?? {};

  const internalApiRef = useGridApiRef();
  const gridApiRef = providedApiRef ?? internalApiRef;

  const {
    effectiveColumnVisibilityModel: gridColumnVisibilityModel,
    handleColumnOrderChange: handleGridColumnOrderChange,
    handleColumnVisibilityModelChange: handleGridColumnVisibilityModelChange,
    handleColumnWidthChange: handleGridColumnWidthChange,
    orderedColumns: gridOrderedColumns,
  } = useGridColumnState({
    columns,
    gridApiRef,
    settings,
    defaultColumnVisibilityModel: defaultConfig.columnVisibilityModel,
    tableKey,
    updateSettings,
  });

  const handleScrollPositionChange = useCallback(
    (params) => {
      if (typeof providedOnScrollPositionChange === 'function') {
        providedOnScrollPositionChange(params);
      }
      // If user scrolls back to the extreme left, ensure first column is in view
      if (params?.left != null && params.left <= 1) {
        gridApiRef.current?.scrollToIndexes?.({ colIndex: 0, rowIndex: 0 });
      }
    },
    [providedOnScrollPositionChange, gridApiRef]
  );

  const mergedInitialState = useMemo(
    () => ({
      pinnedColumns: {
        left: ['__view__'],
      },
      ...(providedInitialState ?? {}),
      ...(serverSideFiltering && effectiveFilterModel
        ? {
            filter: {
              ...(providedInitialState?.filter ?? {}),
              filterModel: effectiveFilterModel,
            },
          }
        : {}),
    }),
    [effectiveFilterModel, providedInitialState, serverSideFiltering]
  );

  const mergedSlots = {
    toolbar: CustomToolbar,
    loadingOverlay: LottieLoadingOverlay, // Default Lottie loading overlay
    ...(providedSlots ?? {}),
    ...(tableProps?.slots ?? {}),
    ...(directSlots ?? {}),
    ...(providedComponents?.Toolbar
      ? { toolbar: providedComponents.Toolbar }
      : {}),
  };

  const mergedSlotProps = {
    ...(providedSlotProps ?? {}),
    toolbar: {
      ...(providedSlotProps?.toolbar ?? providedComponentsProps?.toolbar ?? {}),
      showExport,
      customExport: tableProps?.customExport,
      showQuickFilter: showQuickFilterProp,
      quickFilterProps: quickFilterPropsProp ?? { debounceMs: 250 },
      onResetView: tableKey
        ? () => {
            updateSettings(defaultConfig);
          }
        : undefined,
    },
  };

  const pageSizeOptions = providedPageSizeOptions ?? [25, 50, 100];

  const { sortModel, handleSortModelChange } = useGridSortState({
    serverSideSorting,
    setSortModel,
    settingsSortModel: settings.sortModel,
    tableKey,
    updateSettings,
  });

  useInitialServerSortSync({
    initialSortModel: settings.sortModel,
    serverSideSorting,
    setSortModel,
    settingsLoading,
    tableKey,
  });

  return (
    <DataGridPremium
      apiRef={gridApiRef}
      localeText={getDataGridLocaleText(currentLocale)}
      rows={rows}
      columns={gridOrderedColumns}
      loading={isLoading || settingsLoading}
      filterMode={serverSideFiltering ? 'server' : 'client'}
      sortingMode={serverSideSorting ? 'server' : 'client'}
      paginationMode={paginationMode ?? 'client'}
      {...(paginationMode === 'server' || rowCount !== undefined
        ? { rowCount: rowCount ?? 0 }
        : {})}
      filterModel={disableColumnFilter ? undefined : gridFilterModel}
      onFilterModelChange={handleFilterModelChange}
      filterDebounceMs={500}
      columnBuffer={6}
      columnBufferPx={500}
      disableVirtualization={providedDisableVirtualization ?? false}
      disableColumnVirtualization={providedDisableColumnVirtualization}
      onScrollPositionChange={handleScrollPositionChange}
      {...restOther}
      sx={{
        border: 0, // Moved from makeStyles
        height: height || 'calc(100vh - 160px)',
        width: '100%',
        minHeight: gridMinHeight, // fallback
        '& .MuiDataGrid-main': {
          // ensures inner div gets height
          height: '100% !important',
        },
        ...(tableProps?.sx ?? {}),
        ...(providedSx ?? {}),
      }}
      slots={mergedSlots}
      initialState={mergedInitialState}
      slotProps={mergedSlotProps}
      paginationModel={providedPaginationModel}
      onPaginationModelChange={providedOnPaginationModelChange}
      pageSizeOptions={pageSizeOptions}
      columnVisibilityModel={gridColumnVisibilityModel}
      onColumnVisibilityModelChange={handleGridColumnVisibilityModelChange}
      onColumnWidthChange={handleGridColumnWidthChange}
      {...(sortModel ? { sortModel } : {})}
      onSortModelChange={handleSortModelChange}
      onColumnOrderChange={handleGridColumnOrderChange}
      onStateChange={providedOnStateChange}
      showToolbar
      pivoting={pivoting}
    />
  );
};

export default function TableView({
  smallTitle = false,
  serverSideFiltering = false, // flag to enable server-side filtering
  setFilterModel = () => {},
  serverSideSorting = false, // flag to enable server-side sorting
  setSortModel = () => {},
  title,
  resolvePath,
  selected,
  form,
  injectHeader,
  action,
  rows,
  columns,
  isLoading,
  breadcrumbs,
  showExport = false,
  wrapActionToNextLineOnMobile,
  renderOnlyTable = false,
  height = 'calc(100vh - 160px)',
  gridMinHeight,
  onPaginationChange,
  slots: directSlots,
  pivoting = false,
  ...other
}) {
  const makeOpenRowButton = useOpenRowDataGrid();
  const memoizedColumns = useMemo(() => {
    return resolvePath
      ? [
          makeOpenRowButton(
            (params) => resolvePath(params.id, params.row),
            other.customActions // Pass customActions from other props
          ),
          ...columns,
        ]
      : columns;
  }, [resolvePath, columns, other.customActions]);

  if (renderOnlyTable) {
    return (
      <TableGrid
        height={height}
        rows={rows}
        columns={memoizedColumns}
        isLoading={isLoading}
        serverSideFiltering={serverSideFiltering}
        setFilterModel={setFilterModel}
        serverSideSorting={serverSideSorting}
        setSortModel={setSortModel}
        showExport={showExport}
        other={other}
        gridMinHeight={gridMinHeight}
        onPaginationChange={onPaginationChange}
        slots={directSlots}
        pivoting={pivoting}
      />
    );
  }

  return (
    <Page
      title={
        typeof title === 'string' && !smallTitle
          ? `${title} | Careit`
          : undefined
      }
    >
      {form ? form() : null}

      <Box display={selected ? 'none' : 'block'}>
        {title || action ? (
          wrapActionToNextLineOnMobile ? (
            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              columnGap={2}
              rowGap={1}
              mb={1}
            >
              {title && (
                <Typography
                  fontWeight={smallTitle ? '700' : undefined}
                  variant={smallTitle ? 'h6' : 'h4'}
                >
                  {title}
                </Typography>
              )}
              <Box flex="1 0 auto">{action}</Box>
            </Box>
          ) : (
            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              justifyContent="space-between"
              columnGap={2}
              rowGap={1}
              mb={1}
            >
              {title && (
                <Typography
                  fontWeight={smallTitle ? '700' : undefined}
                  variant={smallTitle ? 'h6' : 'h4'}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {title}
                </Typography>
              )}
              {action}
            </Box>
          )
        ) : null}

        {breadcrumbs && <MBreadcrumbs links={breadcrumbs} />}

        {injectHeader}

        <TableGrid
          height={height}
          rows={rows}
          columns={memoizedColumns}
          isLoading={isLoading}
          serverSideFiltering={serverSideFiltering}
          setFilterModel={setFilterModel}
          serverSideSorting={serverSideSorting}
          setSortModel={setSortModel}
          showExport={showExport}
          other={other}
          onPaginationChange={onPaginationChange}
          slots={directSlots}
          pivoting={pivoting}
        />
      </Box>
    </Page>
  );
}
